import { useMemo } from 'react';
import { useDateNow, useReduxState } from 'hooks';
import { format } from 'date-fns';
import { getFormattedDate } from 'lib/common';
import { Badge, DisplayFormattedDate, User } from 'components/common';
import { AccessRequests } from 'components/common/accessRequest';
import { BoxTableCell } from 'components/common/box';
import {
  AchievementsIndividualDelete,
  AchievementsIndividualEdit,
  AchievementsIndividualViewInfoModal
} from '.';

import type { ApiDataHookUpdateHandler } from 'lib/api/useApiData';
import type {
  AccessRequestActions,
  AccessRequestBody,
  AccessRequestHeader
} from 'components/common/accessRequest/AccessRequests';

interface Props {
  getUpdateHandler: (func: ApiDataHookUpdateHandler) => void;
  updateUsers: () => Promise<void>;
}

const AchievementsIndividualAccessRequests = ({
  getUpdateHandler,
  updateUsers
}: Props) => {
  const { dateNow: now } = useDateNow();

  const {
    messages,
    serverConfig: { individualAchievementsUpcomingEnabled },
    theme,
    user
  } = useReduxState();

  const handleRequestAction = async () => {
    await updateUsers();
  };

  const Header = useMemo<AccessRequestHeader>(
    () => () =>
      (
        <>
          <BoxTableCell>{messages.commonTitle}</BoxTableCell>
          {individualAchievementsUpcomingEnabled ? (
            <BoxTableCell>{messages.commonType}</BoxTableCell>
          ) : null}
          <BoxTableCell>{messages.commonUser}</BoxTableCell>
          <BoxTableCell>{messages.courseAccessRequestDate}</BoxTableCell>
          <BoxTableCell>{messages.completionsCompletionDate}</BoxTableCell>
          <BoxTableCell>{messages.commonExpirationDate}</BoxTableCell>
          <BoxTableCell shrink={true} />
        </>
      ),
    []
  );

  const Body = useMemo<AccessRequestBody>(
    () =>
      ({ userData }) => {
        const isUpcoming =
          userData.completionDate && Date.parse(userData.completionDate) > now;

        return (
          <>
            <BoxTableCell>
              <div style={{ minWidth: 100 }}>{userData.title}</div>
            </BoxTableCell>

            {individualAchievementsUpcomingEnabled ? (
              <BoxTableCell>
                {isUpcoming ? (
                  <Badge
                    category="warning-light"
                    size="small"
                    style={{ position: 'relative', top: -1 }}
                    text={messages.commonUpcoming}
                  />
                ) : (
                  <Badge
                    category="success-light"
                    size="small"
                    style={{ position: 'relative', top: -1 }}
                    text={messages.commonCompleted}
                  />
                )}
              </BoxTableCell>
            ) : null}

            <BoxTableCell>
              <User
                enableProfileLink={userData?.User.id !== user?.id}
                style={{ minWidth: 200 }}
                user={userData.User}
              />
            </BoxTableCell>

            <BoxTableCell icon={['far', 'calendar-alt']}>
              <DisplayFormattedDate date={userData.createdAt} />
            </BoxTableCell>

            <BoxTableCell>
              <span
                className="date-completed"
                style={
                  isUpcoming ? { color: theme.warningStandaloneColor } : {}
                }
              >
                {userData.completionDate
                  ? getFormattedDate({ date: userData.completionDate })
                  : '-'}
              </span>
            </BoxTableCell>

            <BoxTableCell>
              <span
                className={`${
                  Date.parse(userData.expirationDate as string) < now
                    ? 'date-expired'
                    : ''
                }`}
              >
                {userData.expirationDate
                  ? format(new Date(userData.expirationDate), 'dd.MM.yyyy')
                  : '-'}
              </span>
            </BoxTableCell>

            <BoxTableCell shrink={true}>
              <AchievementsIndividualViewInfoModal
                individualAchievement={userData}
                user={userData.User}
              />
            </BoxTableCell>

            <style jsx>{`
              .date-completed {
                color: ${theme.successStandaloneColor};
              }
              .date-expired {
                color: ${theme.dangerColor};
              }
            `}</style>
          </>
        );
      },
    [now]
  );

  const Actions = useMemo<AccessRequestActions>(
    () =>
      ({ update, userData }) =>
        (
          <>
            <AchievementsIndividualEdit
              individualAchievement={userData}
              isRequest={true}
              updateData={update}
            />
            {userData?.User.id === user?.id && (
              <AchievementsIndividualDelete
                individualAchievement={userData}
                isRequest={true}
                updateData={update}
              />
            )}
          </>
        ),
    []
  );

  return (
    <AccessRequests
      customTableElements={{ Actions, Header, Body }}
      getUpdateHandler={getUpdateHandler}
      hideBatchActions={!user?.roles.includes('superadmin')}
      hideDefaultColumns={true}
      hideTableIfNoRequests={true}
      onRequestAction={handleRequestAction}
      searchPlaceholder={messages.achievementSearch}
      title={messages.commonPendingRequests}
      url="/admin/achievements-individual/access-request"
    />
  );
};

export default AchievementsIndividualAccessRequests;
